import React, { useEffect, useMemo, useState } from "react";
import { PageHeader, DataTable, StatusBadge } from "@/components/common";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, UserCircle2, ShieldCheck } from "lucide-react";

const emptyForm = () => ({ id: null, municipality_id: "", name: "", email: "", password: "", status: "active" });

export default function UsersPage() {
  const [rows, setRows] = useState([]);
  const [muns, setMuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [filterMun, setFilterMun] = useState("all");

  async function load() {
    setLoading(true);
    try {
      const [u, m] = await Promise.all([api.get("/admin/users"), api.get("/admin/municipalities")]);
      setRows(u.data); setMuns(m.data);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => (
    filterMun === "all" ? rows : rows.filter((r) => r.municipality_id === filterMun)
  ), [rows, filterMun]);

  function openNew() {
    setForm({ ...emptyForm(), municipality_id: muns[0]?.id || "" });
    setOpen(true);
  }
  function openEdit(r) {
    setForm({ id: r.id, municipality_id: r.municipality_id, name: r.name, email: r.email, password: "", status: r.status || "active" });
    setOpen(true);
  }

  async function save() {
    if (!form.municipality_id) { toast.error("Belediye seçin"); return; }
    if (!form.name || !form.email) { toast.error("İsim ve e-posta zorunludur"); return; }
    if (!form.id && !form.password) { toast.error("Yeni kullanıcı için şifre zorunludur"); return; }
    try {
      if (form.id) {
        const body = { name: form.name, email: form.email, status: form.status };
        if (form.password) body.password = form.password;
        await api.put(`/admin/municipalities/${form.municipality_id}/users/${form.id}`, body);
        toast.success("Kullanıcı güncellendi");
      } else {
        await api.post(`/admin/municipalities/${form.municipality_id}/users`, {
          municipality_id: form.municipality_id,
          name: form.name, email: form.email, password: form.password, status: form.status,
        });
        toast.success("Kullanıcı oluşturuldu");
      }
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  }

  async function remove(r) {
    if (r.role === "super_admin") { toast.error("Süper admin bu ekrandan silinemez"); return; }
    if (!window.confirm(`${r.email} kullanıcısı silinsin mi?`)) return;
    try {
      await api.delete(`/admin/municipalities/${r.municipality_id}/users/${r.id}`);
      toast.success("Silindi"); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        overline="Erişim Yönetimi"
        title="Kullanıcılar"
        description="Belediye kullanıcıları ve sistem yöneticileri. Her belediye için ayrı ayrı hesap tanımlayabilir; kullanıcı sadece kendi belediyesinin panelini ve raporlarını görür."
        actions={
          <Button className="bg-red-600 hover:bg-red-700 rounded-sm h-10 font-heading font-semibold uppercase text-xs tracking-wider" onClick={openNew} data-testid="add-user-button">
            <Plus className="w-4 h-4 mr-1.5" /> Yeni Kullanıcı
          </Button>
        }
      />

      <div className="flex items-center gap-3">
        <Label className="text-xs text-slate-600">Belediye Filtresi</Label>
        <Select value={filterMun} onValueChange={setFilterMun}>
          <SelectTrigger className="w-64 h-9" data-testid="users-mun-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tümü ({rows.length})</SelectItem>
            {muns.map((m) => {
              const cnt = rows.filter((r) => r.municipality_id === m.id).length;
              return <SelectItem key={m.id} value={m.id}>{m.name} ({cnt})</SelectItem>;
            })}
          </SelectContent>
        </Select>
      </div>

      {loading ? <div className="text-sm text-slate-500">Yükleniyor…</div> : (
        <DataTable
          testid="users-table"
          columns={[
            {
              label: "Kullanıcı", render: (r) => (
                <div className="flex items-center gap-2">
                  {r.role === "super_admin"
                    ? <ShieldCheck className="w-4 h-4 text-red-600" />
                    : <UserCircle2 className="w-4 h-4 text-slate-400" />}
                  <div>
                    <div className="font-medium">{r.name}</div>
                    <div className="text-xs text-slate-500 font-mono">{r.email}</div>
                  </div>
                </div>
              )
            },
            {
              label: "Rol",
              render: (r) => (
                <span className={`text-[10.5px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-sm border ${r.role === "super_admin" ? "bg-red-50 text-red-700 border-red-200" : "bg-slate-100 text-slate-700 border-slate-200"}`}>
                  {r.role === "super_admin" ? "Süper Admin" : "Belediye Kullanıcısı"}
                </span>
              )
            },
            { label: "Belediye", render: (r) => r.municipality_name || <span className="text-slate-400">—</span> },
            { label: "Durum", render: (r) => <StatusBadge status={r.status || "active"} /> },
            { label: "Oluşturulma", render: (r) => <span className="text-xs font-mono">{(r.created_at || "").slice(0, 10)}</span> },
            {
              label: "İşlemler", render: (r) => (
                <div className="flex gap-1">
                  {r.role !== "super_admin" && (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => openEdit(r)} data-testid={`edit-user-${r.id}`}><Pencil className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => remove(r)} data-testid={`delete-user-${r.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </>
                  )}
                </div>
              )
            },
          ]}
          rows={filtered}
          empty="Kullanıcı bulunamadı."
        />
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md rounded-md">
          <DialogHeader>
            <DialogTitle className="font-heading">{form.id ? "Kullanıcıyı Düzenle" : "Yeni Kullanıcı"}</DialogTitle>
            <DialogDescription>Belediyeye bağlı panel kullanıcısı oluşturun veya düzenleyin. Kullanıcı yalnızca kendi belediyesinin verilerine erişebilir.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Belediye</Label>
              <Select value={form.municipality_id} onValueChange={(v) => setForm({ ...form, municipality_id: v })} disabled={!!form.id}>
                <SelectTrigger data-testid="user-form-mun"><SelectValue placeholder="Belediye seçin" /></SelectTrigger>
                <SelectContent>{muns.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
              </Select>
              {form.id && <div className="text-[10px] text-slate-500 mt-1">Belediye değişikliği için kullanıcıyı silip yeniden oluşturun.</div>}
            </div>
            <div><Label>Ad Soyad</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-form-name" /></div>
            <div><Label>E-posta</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-form-email" /></div>
            <div>
              <Label>Şifre {form.id && <span className="text-[10px] text-slate-500 ml-1">(boş bırakırsanız değişmez)</span>}</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={form.id ? "••••••••" : ""} data-testid="user-form-password" />
            </div>
            {form.id && (
              <div>
                <Label>Durum</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="user-form-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Aktif</SelectItem>
                    <SelectItem value="paused">Duraklatıldı</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>İptal</Button>
            <Button onClick={save} className="bg-red-600 hover:bg-red-700 rounded-sm" data-testid="user-form-save">
              {form.id ? "Güncelle" : "Oluştur"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
